import {Fragment, useEffect, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {
    Alert,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    ListItemText,
    MenuItem,
    MenuList,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material'
import {Icon} from '@iconify/react'
import LaunchIcon from '@mui/icons-material/Launch'
import {alpha, Theme} from '@mui/material/styles'
import {HttpSystems} from 'src/http/workflow/httpSystems'
import {AprobacionArchivo, AprobacionMensaje, HttpAprobaciones} from 'src/http/workflow/httpAprobaciones'
import {HttpFiles} from 'src/http/files/httpFiles'
import {downloadFileBlobHelper} from 'src/utils/helpers'
import {dateFormatter} from 'src/utils/formatters'
import {fileFormatter} from 'src/utils/formatters/fileFormatter'
import {Sistema} from 'src/pages/messages/types'
import {SistemaPrincipal, SistemaPrincipalFields} from 'src/types/workflow/cache'
import {EntityListWithPaginationFields, EntityPagination, EntityPaginationFields} from 'src/types'
import {rutaPorCodSistema} from 'src/pages/sistema/RefactorLayout'
import toast from 'react-hot-toast'

const PAGE_SIZE = 12
const fechaCellSx = {
    width: 136,
    minWidth: 136,
    maxWidth: 136,
    px: 1,
    whiteSpace: 'nowrap'
} as const

const menuHeaderSx = (theme: Theme) => ({
    px: 2,
    py: 1,
    bgcolor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
})

const menuSectionSx = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1
} as const

const paisSectionSx = {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 auto'
} as const

const menuScrollSx = {
    flex: 1,
    minHeight: 0,
    maxHeight: {xs: 220, md: 'none'},
    overflowY: 'auto',
    py: 0.5
} as const

const paisScrollSx = {
    height: 136,
    maxHeight: 136,
    overflowY: 'auto',
    py: 0.5
} as const

const workflowDividerSx = {
    mx: 1.5,
    my: 0.25,
    borderColor: 'divider'
} as const

const menuSectionDividerSx = {
    my: 1,
    borderColor: 'primary.light',
    borderBottomWidth: 2
} as const

const menuItemSx = (theme: Theme, multiline = false) => ({
    border: '1px solid transparent',
    borderLeftWidth: 3,
    mx: 1,
    my: 0.25,
    minHeight: multiline ? 44 : 32,
    px: 1.25,
    py: multiline ? 0.75 : 0.25,
    borderRadius: 1,
    alignItems: multiline ? 'flex-start' : 'center',
    color: theme.palette.text.secondary,
    '& .MuiListItemText-root': {
        my: 0
    },
    '& .MuiListItemText-primary': {
        fontSize: '0.8125rem',
        fontWeight: 500,
        lineHeight: 1.25,
        overflow: multiline ? 'visible' : 'hidden',
        textOverflow: multiline ? 'clip' : 'ellipsis',
        whiteSpace: multiline ? 'normal' : 'nowrap'
    },
    '&.Mui-selected': {
        bgcolor: alpha(theme.palette.primary.light, 0.22),
        borderColor: theme.palette.primary.light,
        color: theme.palette.primary.dark,
        '& .MuiListItemText-primary': {
            fontWeight: 700
        },
        '&:hover': {
            bgcolor: alpha(theme.palette.primary.light, 0.32)
        }
    },
    '&:hover': {
        bgcolor: alpha(theme.palette.primary.light, 0.12),
        color: theme.palette.primary.main
    }
})

const ordenarMensajesPorIdDesc = (mensajes: AprobacionMensaje[]) =>
    [...mensajes].sort((mensajeA, mensajeB) => mensajeB.id - mensajeA.id)

const Aprobaciones = () => {
    const navigate = useNavigate()
    const [sistemasPrincipales, setSistemasPrincipales] = useState<SistemaPrincipal[]>([])
    const [sistemaPrincipalSeleccionado, setSistemaPrincipalSeleccionado] = useState<SistemaPrincipal>()
    const [sistemas, setSistemas] = useState<Sistema[]>([])
    const [sistemaSeleccionado, setSistemaSeleccionado] = useState<Sistema>()
    const [mensajes, setMensajes] = useState<AprobacionMensaje[]>([])
    const [paginacion, setPaginacion] = useState<EntityPagination>()
    const [actualPage, setActualPage] = useState(1)
    const [loadingSistemasPrincipales, setLoadingSistemasPrincipales] = useState(true)
    const [loadingSistemas, setLoadingSistemas] = useState(false)
    const [loadingMensajes, setLoadingMensajes] = useState(false)
    const [errorSistemasPrincipales, setErrorSistemasPrincipales] = useState(false)
    const [errorSistemas, setErrorSistemas] = useState(false)
    const [errorMensajes, setErrorMensajes] = useState(false)

    useEffect(() => {
        setLoadingSistemasPrincipales(true)
        HttpSystems.getSystemPrincipalsByUser()
            .then(res => {
                setSistemasPrincipales(res)
                setSistemaPrincipalSeleccionado(res[0])
                setErrorSistemasPrincipales(false)
            })
            .catch(() => setErrorSistemasPrincipales(true))
            .finally(() => setLoadingSistemasPrincipales(false))
    }, [])

    useEffect(() => {
        if (!sistemaPrincipalSeleccionado) {
            setSistemas([])
            setSistemaSeleccionado(undefined)

            return
        }

        setLoadingSistemas(true)
        setErrorSistemas(false)
        setSistemas([])
        setSistemaSeleccionado(undefined)
        setMensajes([])
        setPaginacion(undefined)
        setActualPage(1)

        HttpSystems.getByPrincipalForUser(sistemaPrincipalSeleccionado.id)
            .then(res => {
                setSistemas(res)
                setSistemaSeleccionado(res[0])
            })
            .catch(() => setErrorSistemas(true))
            .finally(() => setLoadingSistemas(false))
    }, [sistemaPrincipalSeleccionado])

    useEffect(() => {
        if (!sistemaSeleccionado) return

        setLoadingMensajes(true)
        setErrorMensajes(false)

        HttpAprobaciones.getMensajesAprobadosPorSistema(sistemaSeleccionado.id, {
            [EntityPaginationFields.ActualPage]: actualPage,
            [EntityPaginationFields.PageSize]: PAGE_SIZE
        })
            .then(response => {
                setMensajes(ordenarMensajesPorIdDesc(response[EntityListWithPaginationFields.List]))
                setPaginacion(response[EntityListWithPaginationFields.Pagination])
            })
            .catch(() => {
                setMensajes([])
                setPaginacion(undefined)
                setErrorMensajes(true)
            })
            .finally(() => setLoadingMensajes(false))
    }, [sistemaSeleccionado, actualPage])

    const tituloMensajes = useMemo(() => {
        if (!sistemaSeleccionado) return 'Mensajes aprobados'

        return `Mensajes aprobados - ${sistemaSeleccionado.descripcion}`
    }, [sistemaSeleccionado])

    const abrirMensaje = (mensaje: AprobacionMensaje) => {
        const rutaSistema = rutaPorCodSistema[mensaje.codSistemaTipo]

        if (!rutaSistema) {
            toast.error('No se encontro la ruta para abrir la operacion')

            return
        }

        navigate(`/operacion/${rutaSistema}/${mensaje.id}`)
    }

    const descargarArchivo = (archivo: AprobacionArchivo) => {
        const promise = HttpFiles.download(archivo.id)

        toast.promise(promise, {
            loading: 'Buscando archivo',
            error: 'Error al descargar archivo',
            success: 'Archivo listo para descarga'
        }).then(downloadFileBlobHelper)
    }

    return (
        <Stack spacing={3}>
            <Stack direction='row' alignItems='center' spacing={1}>
                <Icon icon='mdi:check-decagram-outline' fontSize={24} />
                <Typography variant='h5' fontWeight={700}>
                    Aprobaciones
                </Typography>
            </Stack>

            <Stack direction={{xs: 'column', md: 'row'}} spacing={3} alignItems='stretch'>
                <Paper
                    variant='outlined'
                    sx={{
                        width: {xs: '100%', md: 320},
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        height: {md: 'calc(100vh - 220px)'},
                        minHeight: {md: 420}
                    }}
                >
                    <Box sx={paisSectionSx}>
                        <Box sx={menuHeaderSx}>
                            <Typography variant='subtitle2' fontWeight={700} color='inherit'>
                                Pais
                            </Typography>
                        </Box>
                        <Divider />

                        <Box sx={paisScrollSx}>
                            {loadingSistemasPrincipales && (
                                <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                                    <CircularProgress size={24} />
                                </Box>
                            )}

                            {errorSistemasPrincipales && (
                                <Alert severity='error' sx={{m: 1}}>No se pudieron cargar los paises.</Alert>
                            )}

                            {!loadingSistemasPrincipales && !errorSistemasPrincipales && (
                                <MenuList dense disablePadding>
                                    {sistemasPrincipales.map(sistemaPrincipal => (
                                        <Tooltip
                                            title={sistemaPrincipal.descripcion}
                                            placement='right'
                                            key={sistemaPrincipal.id}
                                        >
                                            <MenuItem
                                                selected={sistemaPrincipalSeleccionado?.id === sistemaPrincipal.id}
                                                onClick={() => setSistemaPrincipalSeleccionado(sistemaPrincipal)}
                                                sx={menuItemSx}
                                            >
                                                <Avatar
                                                    src={sistemaPrincipal[SistemaPrincipalFields.SrcIcono]}
                                                    sx={{width: 20, height: 20, mr: 1, fontSize: '0.7rem'}}
                                                >
                                                    {sistemaPrincipal.descripcion?.charAt(0)}
                                                </Avatar>
                                                <ListItemText primary={sistemaPrincipal.descripcion} />
                                            </MenuItem>
                                        </Tooltip>
                                    ))}
                                </MenuList>
                            )}
                        </Box>
                    </Box>

                    <Divider sx={menuSectionDividerSx} />

                    <Box sx={menuSectionSx}>
                        <Box sx={menuHeaderSx}>
                            <Typography variant='subtitle2' fontWeight={700} color='inherit'>
                                Workflows
                            </Typography>
                        </Box>
                        <Divider />

                        <Box sx={menuScrollSx}>
                            {loadingSistemas && (
                                <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                                    <CircularProgress size={24} />
                                </Box>
                            )}

                            {errorSistemas && (
                                <Alert severity='error' sx={{m: 1}}>No se pudieron cargar los sistemas.</Alert>
                            )}

                            {!loadingSistemas && !errorSistemas && (
                                <MenuList dense disablePadding>
                                    {sistemas.map((sistema, index) => (
                                        <Fragment key={sistema.id}>
                                            <Tooltip title={sistema.descripcion} placement='right'>
                                                <MenuItem
                                                    selected={sistemaSeleccionado?.id === sistema.id}
                                                    onClick={() => {
                                                        setSistemaSeleccionado(sistema)
                                                        setActualPage(1)
                                                    }}
                                                    sx={theme => menuItemSx(theme, true)}
                                                >
                                                    <ListItemText primary={sistema.descripcion} />
                                                </MenuItem>
                                            </Tooltip>
                                            {index < sistemas.length - 1 && (
                                                <Divider component='li' sx={workflowDividerSx} />
                                            )}
                                        </Fragment>
                                    ))}
                                </MenuList>
                            )}
                        </Box>
                    </Box>
                </Paper>

                <Paper variant='outlined' sx={{flex: 1, minWidth: 0, overflow: 'hidden'}}>
                    <Box sx={{px: 3, py: 2}}>
                        <Typography variant='subtitle1' fontWeight={700}>
                            {tituloMensajes}
                        </Typography>
                    </Box>
                    <Divider />

                    {loadingMensajes && (
                        <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                            <CircularProgress />
                        </Box>
                    )}

                    {errorMensajes && <Alert severity='error'>No se pudieron cargar los mensajes aprobados.</Alert>}

                    {!loadingMensajes && !errorMensajes && mensajes.length === 0 && (
                        <Alert severity='info'>No hay mensajes aprobados para este sistema.</Alert>
                    )}

                    {!loadingMensajes && !errorMensajes && mensajes.length > 0 && (
                        <>
                            <TableContainer>
                                <Table size='small'>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Id</TableCell>
                                            <TableCell sx={fechaCellSx}>Fecha de aprobacion</TableCell>
                                            <TableCell>Referencia</TableCell>
                                            <TableCell>Archivos</TableCell>
                                            <TableCell align='right'>Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {mensajes.map(mensaje => (
                                            <TableRow key={mensaje.id} hover>
                                                <TableCell>{mensaje.id}</TableCell>
                                                <TableCell sx={fechaCellSx}>
                                                    {mensaje.fechaAprobacion
                                                        ? dateFormatter.toShortDate(mensaje.fechaAprobacion)
                                                        : '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title={mensaje.referencia}>
                                                        <Typography variant='body2' noWrap maxWidth={360}>
                                                            {mensaje.referencia}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction='row' spacing={0.5} flexWrap='wrap'>
                                                        {(mensaje.archivos || []).map(archivo => (
                                                            <Tooltip
                                                                title={archivo.descArchivoTipo || archivo.nombreArchivo}
                                                                key={archivo.id}
                                                            >
                                                                <IconButton size='small' onClick={() => descargarArchivo(archivo)}>
                                                                    {fileFormatter.getIconByFileName(archivo.nombreArchivo, {fontSize: 'small'})}
                                                                </IconButton>
                                                            </Tooltip>
                                                        ))}
                                                        {!mensaje.archivos?.length && (
                                                            <Typography variant='caption' color='text.secondary'>
                                                                Sin adjuntos
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align='right'>
                                                    <Tooltip title='Abrir operacion'>
                                                        <Button
                                                            size='small'
                                                            variant='outlined'
                                                            startIcon={<LaunchIcon />}
                                                            onClick={() => abrirMensaje(mensaje)}
                                                        >
                                                            Abrir
                                                        </Button>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {paginacion && (
                                <TablePagination
                                    component='div'
                                    count={paginacion[EntityPaginationFields.CantRecords]}
                                    page={paginacion[EntityPaginationFields.ActualPage] - 1}
                                    rowsPerPage={paginacion[EntityPaginationFields.PageSize]}
                                    rowsPerPageOptions={[PAGE_SIZE]}
                                    labelRowsPerPage='Filas por pagina'
                                    onPageChange={(_, page) => setActualPage(page + 1)}
                                    onRowsPerPageChange={() => undefined}
                                />
                            )}
                        </>
                    )}
                </Paper>
            </Stack>
        </Stack>
    )
}

export default Aprobaciones
